<template>
  <b-modal :id="id" :title="title" size="xl" modal-class="audit-control-modal-fullscreen" scrollable hide-footer body-class="p-0" @show="onShow">
    <div class="d-flex h-100">
      <!-- Sidebar Filters: Fixed width -->
      <div v-show="showFilters" class="h-100">
        <AuditControlFilterSidebar class="h-100" @input="debounceSearch" @reset="resetFilters" />
      </div>

      <!-- Main Content: Results -->
      <div class="flex-grow-1 d-flex flex-column h-100 position-relative">
        <!-- VIEW MODE: LIST -->
        <div v-if="viewMode === 'LIST'" class="d-flex flex-column h-100">
          <!-- Header Actions -->
          <div class="p-3 border-bottom d-flex justify-content-between align-items-center bg-white">
            <div class="d-flex align-items-center">
              <b-button variant="light" size="sm" class="mr-3" @click="showFilters = !showFilters" v-b-tooltip.hover title="Filtreleri Göster/Gizle">
                <i class="bx bx-filter-alt"></i>
              </b-button>
              <div class="d-flex align-items-center">
                <span class="font-weight-bold">{{ totalDocs }}</span> <span class="ml-1 mr-2">{{ $t("common.results-found") }}</span>
                <span v-if="selectedList.length > 0" class="text-primary font-weight-bold mr-3"> ({{ selectedList.length }} {{ $t("common.selected") }}) </span>
                <b-form-checkbox v-if="selectedList.length > 0" v-model="showOnlySelected" switch class="mb-0 ml-2">
                  <span class="small font-weight-bold text-muted">{{ $t("common.show-all-selected") }}</span>
                </b-form-checkbox>
              </div>
            </div>
            <div>
              <b-button v-if="!hideCreatedButton" variant="primary" class="mr-2" @click="goToCreate"> <i class="bx bx-plus"></i> {{ $t("audit-control.control.new") }} </b-button>
              <b-button variant="success" @click="confirmSelection">
                <i class="bx bx-check mr-1"></i><span v-if="addButtonText">{{ addButtonText }}</span> <span v-else>{{ $t("audit-control.control.save-selection") }}</span>
              </b-button>
              <b-button variant="secondary" class="ml-2" @click="$bvModal.hide(id)">
                {{ $t("common.close") }}
              </b-button>
            </div>
          </div>

          <!-- Table List -->
          <div class="p-3 flex-grow-1 overflow-auto bg-white position-relative">
            <content-loading v-if="isLoading || bulkLoading" />

            <table class="table table-hover table-bordered table-striped">
              <thead class="thead-light sticky-top" style="top: -1rem;">
                <tr>
                  <th style="width: 140px;">
                    <b-form-checkbox
                      :checked="selectedIds.length === totalDocs && totalDocs > 0"
                      :indeterminate="selectedIds.length > 0 && selectedIds.length < totalDocs"
                      @change="toggleAllInPage"
                    >
                      <span class="font-weight-normal small">{{
                        selectedIds.length === totalDocs && totalDocs > 0 ? $t("audit-control.control.clear-selection") : $t("audit-control.control.select-all")
                      }}</span>
                    </b-form-checkbox>
                  </th>
                  <th>{{ $t("audit-control.control.no") }}</th>
                  <th>{{ $t("audit-control.control.name") }}</th>
                  <th>{{ $t("audit-control.control.class") }}</th>
                  <th>{{ $t("audit-control.control.category") }}</th>
                  <th>{{ $t("audit-control.control.criticality") }}</th>
                  <th>{{ $t("audit-control.control.status") }}</th>
                  <th>{{ $t("audit-control.control.type") }}</th>
                  <th>{{ $t("compliance-item.compliance") }}</th>
                  <th>{{ $t("audit-control.control.last-test") }}</th>
                  <th style="width: 50px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="control in displayedControls" :key="control._id" @click="toggleSelection(control)" class="cursor-pointer">
                  <td>
                    <b-form-checkbox :checked="isSelected(control)" @click.native.stop @change="() => toggleSelection(control)"></b-form-checkbox>
                  </td>
                  <td>{{ control.no }}</td>
                  <td>{{ control.name | translate }}</td>
                  <td>{{ control.class ? $t("audit-control.class." + control.class.toLowerCase()) : "" }}</td>
                  <td>
                    <span v-if="control.category">{{ control.category.name | translate }}</span>
                  </td>
                  <td>
                    <b-badge :variant="getCriticalityVariant(control.criticality)">
                      {{ control.criticality ? $t("audit-control.criticality." + control.criticality.toLowerCase()) : "" }}
                    </b-badge>
                  </td>
                  <td>{{ control.status ? $t("audit-control.status." + control.status.toLowerCase()) : "" }}</td>
                  <td>
                    <span v-if="control.type">{{ control.type.name | translate }}</span>
                  </td>
                  <td style="max-width: 300px;">
                    <div v-if="control.complianceItems && control.complianceItems.length > 0">
                      <div v-for="item in getDisplayItems(control)" :key="item._id" class="mb-1" :title="item.description">
                        <b-badge variant="light" class="text-wrap text-left border w-100 d-block">
                          <span class="small mr-1">{{ item.compliance }}</span>
                          <span v-if="item.no" class="font-weight-bold mr-1">{{ item.no }}</span>
                          <span class="small">{{ item.name | translate }}</span>
                        </b-badge>
                      </div>
                      <div v-if="getHiddenCount(control) > 0">
                        <b-badge :id="'popover-target-' + control._id" variant="light" class="small text-muted font-italic pl-1 cursor-pointer border">
                          + {{ getHiddenCount(control) }} {{ $t("common.more") }}
                        </b-badge>

                        <b-popover :target="'popover-target-' + control._id" triggers="hover focus" placement="auto" boundary="viewport" custom-class="shadow-sm">
                          <template #title>{{ $t("compliance-item.compliance") }}</template>
                          <div style="max-height: 300px; overflow-y: auto; max-width: 300px">
                            <div v-for="(items, standard) in getHiddenItemsGrouped(control)" :key="standard" class="mb-2">
                              <div class="font-weight-bold text-primary border-bottom mb-1 pb-1 small">{{ standard }}</div>
                              <div v-for="item in items" :key="item._id" class="mb-1 pl-1">
                                <b-badge variant="light" class="text-wrap text-left w-100 d-block border-0 p-0 text-dark font-weight-normal">
                                  <small v-if="item.no" class="font-weight-bold mr-1">{{ item.no }}:</small>
                                  <span class="small">{{ item.name | translate }}</span>
                                </b-badge>
                              </div>
                            </div>
                          </div>
                        </b-popover>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div v-if="control.lastTest" class="small">
                      <div class="font-weight-bold">{{ control.lastTest.auditName | translate }}</div>
                      <div>{{ control.lastTest.testDate | date }}</div>
                      <div v-if="control.lastTest.result">
                        <b-badge :variant="control.lastTest.result === 'EFFECTIVE' ? 'success' : 'danger'">
                          {{ $t("audit-control.test.result." + control.lastTest.result.toLowerCase()) }}
                        </b-badge>
                        <span class="ml-1 text-muted" v-if="control.lastTest.findingCount > 0">
                          ({{ control.lastTest.findingCount }} {{ $t("audit-control.finding.title") }})
                        </span>
                      </div>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>

                  <td>
                    <b-button variant="outline-primary" size="sm" class="btn-icon" @click.stop="openRelations(control)">
                      <i class="bx bx-search"></i>
                    </b-button>
                  </td>
                </tr>
                <tr v-if="displayedControls.length === 0">
                  <td colspan="11" class="text-center p-4">
                    {{ $t("common.no-data") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-show="!showOnlySelected" class="p-3">
            <b-pagination
              v-model="request.options.page"
              :total-rows="totalDocs"
              :per-page="request.options.limit"
              align="center"
              class="mb-0"
              @change="fetchControls"
            ></b-pagination>
          </div>
        </div>

        <!-- VIEW MODE: DRILLDOWN (Inline) -->
        <div v-else-if="viewMode === 'DRILLDOWN'" class="d-flex flex-column h-100 bg-white">
          <!-- Breadcrumb Header -->
          <div class="bg-light p-3 border-bottom d-flex align-items-center">
            <b-button variant="outline-secondary" size="sm" class="mr-3" @click="goBack"> <i class="bx bx-arrow-back"></i> {{ $t("common.back") }} </b-button>

            <span class="font-weight-bold mx-2" :class="{ 'text-primary': relationsModal.viewState === 'COMPLIANCES' }">
              {{ relationsModal.currentControl ? relationsModal.currentControl.no : "" }}
              {{ relationsModal.currentControl ? relationsModal.currentControl.name[currentLanguage] || relationsModal.currentControl.name : "" }}
            </span>

            <span v-if="relationsModal.viewState === 'CONTROLS'" class="text-muted mx-2">/</span>
            <span v-if="relationsModal.viewState === 'CONTROLS'" class="font-weight-bold text-primary">
              {{
                relationsModal.currentComplianceItem
                  ? relationsModal.currentComplianceItem.no + " " + (relationsModal.currentComplianceItem.name[currentLanguage] || relationsModal.currentComplianceItem.name)
                  : ""
              }}
            </span>
          </div>

          <!-- Level 2: Compliance List -->
          <div v-if="relationsModal.viewState === 'COMPLIANCES'" class="p-0 flex-grow-1 overflow-auto">
            <table class="table table-hover mb-0">
              <thead class="bg-soft-primary">
                <tr>
                  <th>{{ $t("compliance-item.compliance") }}</th>
                  <th>{{ $t("compliance-item.group") }}</th>
                  <th>{{ $t("compliance-item.no") }}</th>
                  <th>{{ $t("compliance-item.name") }}</th>
                  <th>{{ $t("compliance-item.description") }}</th>
                  <th style="width: 50px;"></th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in relationsModal.complianceItems" :key="item._id">
                  <td>{{ item.compliance }}</td>
                  <td>{{ item.group ? item.group.name : "" | translate }}</td>
                  <td>{{ item.no }}</td>
                  <td>{{ item.name | translate }}</td>
                  <td>{{ item.description | translate }}</td>
                  <td>
                    <b-button variant="outline-info" size="sm" @click="drillDownToControls(item)">
                      {{ $t("audit-control.buttons.related-controls") }} <i class="bx bx-right-arrow-alt"></i>
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!relationsModal.complianceItems || relationsModal.complianceItems.length === 0">
                  <td colspan="4" class="text-center p-3 text-muted">{{ $t("common.no-data") }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Level 3: Related Controls List -->
          <div v-if="relationsModal.viewState === 'CONTROLS'" class="p-0 flex-grow-1 overflow-auto">
            <div v-if="relationsModal.loading" class="text-center p-4"><b-spinner variant="primary"></b-spinner></div>
            <table v-else class="table table-hover mb-0">
              <thead class="bg-soft-info">
                <tr>
                  <th style="width: 50px;"></th>
                  <th>{{ $t("audit-control.control.no") }}</th>
                  <th>{{ $t("audit-control.control.name") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="control in relationsModal.relatedControls" :key="control._id" @click="toggleSelection(control)" class="cursor-pointer">
                  <td>
                    <b-form-checkbox :checked="isSelected(control)" @click.native.stop @change="() => toggleSelection(control)"></b-form-checkbox>
                  </td>
                  <td>{{ control.no }}</td>
                  <td>{{ control.name | translate }}</td>
                </tr>
                <tr v-if="!relationsModal.relatedControls || relationsModal.relatedControls.length === 0">
                  <td colspan="3" class="text-center p-3 text-muted">{{ $t("audit-control.audit.no-synergy-found") }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- VIEW MODE: CREATE -->
        <div v-else-if="viewMode === 'CREATE'" class="d-flex flex-column h-100 bg-white">
          <div class="bg-light p-3 border-bottom d-flex align-items-center">
            <b-button variant="outline-secondary" size="sm" class="mr-3" @click="goBack"> <i class="bx bx-arrow-back"></i> {{ $t("common.back") }} </b-button>
            <span class="font-weight-bold">{{ $t("audit-control.control.new") }}</span>
          </div>
          <div class="p-4 flex-grow-1 overflow-auto">
            <OrganizationControlForm :allowedClass="'AUDIT_ACTIVITY'" :closeOnSave="false" @onSave="onControlSaved" />
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import OrganizationControlForm from "../control/organization-control-form.vue";
import AuditControlFilterSidebar from "./audit-control-filter-sidebar.vue";

export default {
  components: { AuditControlFilterSidebar, OrganizationControlForm },
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "Select Controls",
    },
    addButtonText: {
      type: String,
    },
    hideCreatedButton: {
      type: Boolean,
      default: false,
    },
    preSelectedIds: {
      type: Array,
      default: () => [],
    },
    preSelectedObjects: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      showOnlySelected: false,
      viewMode: "LIST", // 'LIST' or 'DRILLDOWN'
      relationsModal: {
        visible: false,
        viewState: "COMPLIANCES", // 'COMPLIANCES' or 'CONTROLS'
        currentControl: null,
        currentComplianceItem: null,
        complianceItems: [],
        relatedControls: [],
        loading: false,
      },
      createMode: false,
      request: {
        options: { page: 1, limit: 10, sort: { createdAt: -1 } }, // Default sort
        query: {},
      },
      controls: [],
      totalDocs: 0,
      selectedIds: [],
      selectedObjectsMap: {},
      bulkLoading: false,
      showFilters: true,
    };
  },
  watch: {
    selectedIds(newVal) {
      if (newVal.length === 0) {
        this.showOnlySelected = false;
      }
    },
  },
  computed: {
    selectedList() {
      // Return objects from the map based on the current selectedIds array
      return this.selectedIds.map((id) => this.selectedObjectsMap[id]).filter(Boolean);
    },
    displayedControls() {
      if (this.showOnlySelected) {
        return this.selectedList;
      }
      return this.controls;
    },
    allSelectedInPage() {
      if (this.displayedControls.length === 0) return false;
      return this.displayedControls.every((c) => this.selectedIds.includes(c._id));
    },
    isIndeterminate() {
      if (this.displayedControls.length === 0) return false;
      const selectedCount = this.displayedControls.filter((c) => this.selectedIds.includes(c._id)).length;
      return selectedCount > 0 && selectedCount < this.displayedControls.length;
    },
    isLoading() {
      return this.$store.getters["organizationControl/isLoading"];
    },
    currentLanguage() {
      return this.$i18n.locale;
    },
    filters() {
      return this.$store.getters["organizationControl/controlFilters"] || {};
    },
  },
  methods: {
    onShow() {
      this.resetFilters();
      // Initialize selectedIds with preSelectedIds to allow deselection
      this.selectedIds = [...this.preSelectedIds];
      this.selectedObjectsMap = {};

      // Populate the map with preSelectedObjects if provided, so we can emit full objects back
      this.preSelectedObjects.forEach((obj) => {
        if (obj && obj._id) {
          this.$set(this.selectedObjectsMap, obj._id, obj);
        }
      });

      this.fetchControls();
    },
    debounceSearch() {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.request.options.page = 1;
        this.fetchControls();
      }, 500);
    },
    resetFilters() {
      this.viewMode = "LIST";

      this.request.options.page = 1;
      this.fetchControls();
    },
    async fetchControls() {
      const query = {
        class: { value: "AUDIT_ACTIVITY", isObject: true },
        // status: { value: "ACTIVE", isObject: true },
      };

      this.request.query = query;

      try {
        const response = await this.$store.dispatch("organizationControl/fetchFilteredControls", this.request);
        this.controls = response.data.docs;
        this.totalDocs = response.data.totalDocs;
      } catch (e) {
        console.error(e);
      }
    },
    isSelected(control) {
      return this.selectedIds.includes(control._id);
    },
    toggleSelection(control) {
      if (!control || typeof control !== "object") return;

      const index = this.selectedIds.indexOf(control._id);
      if (index > -1) {
        this.selectedIds.splice(index, 1);
        this.$delete(this.selectedObjectsMap, control._id);
      } else {
        if (this.limit > 0 && this.selectedList.length + this.preSelectedIds.length >= this.limit) {
          this.$toast.warning(`Seçim limitine (${this.limit}) ulaşıldı.`);
          return;
        }
        this.selectedIds.push(control._id);
        this.$set(this.selectedObjectsMap, control._id, control);
      }
    },
    async toggleAllInPage(val) {
      if (!val) {
        // Deselect all
        this.selectedIds = [];
        this.selectedObjectsMap = {};
        return;
      }

      // Protect the system from overly large queries/selections
      const MAX_BULK_SELECT_LIMIT = 500;
      if (this.totalDocs > MAX_BULK_SELECT_LIMIT) {
        this.$toast.warning(this.$t("alert.bulk-select-limit", { limit: MAX_BULK_SELECT_LIMIT }));
        return;
      }

      // If we are selecting all, we want to select ALL matching results across all pages

      this.bulkLoading = true;
      try {
        const largeRequest = {
          ...this.request,
          options: { ...this.request.options, page: 1, limit: this.totalDocs },
        };
        const response = await this.$store.dispatch("organizationControl/fetchFilteredControls", largeRequest);
        const allDocs = response.data.docs;

        allDocs.forEach((doc) => {
          if (!this.selectedIds.includes(doc._id)) {
            this.selectedIds.push(doc._id);
          }
          this.$set(this.selectedObjectsMap, doc._id, doc);
        });
      } catch (e) {
        console.error(e);
        this.$toast.error(this.$t("common.fail"));
      } finally {
        this.bulkLoading = false;
      }
    },
    confirmSelection() {
      this.$emit("selected", this.selectedList);
      this.$bvModal.hide(this.id);
    },
    getFilteredItems(control) {
      if (!control.complianceItems) return [];

      const { compliance, complianceItemNo, complianceItemSearch } = this.filters;
      const hasFilter = compliance || complianceItemNo || complianceItemSearch;

      if (!hasFilter) return control.complianceItems;

      return control.complianceItems.filter((item) => {
        let match = true;
        if (compliance && item.compliance !== compliance) match = false;

        if (match && complianceItemNo) {
          const noStr =
            typeof complianceItemNo === "object" && complianceItemNo.name ? complianceItemNo.name.tr || complianceItemNo.name.en || complianceItemNo.name : complianceItemNo;
          if (!item.no || !item.no.toLowerCase().includes(String(noStr).toLowerCase())) match = false;
        }

        if (match && complianceItemSearch) {
          const search = complianceItemSearch.toLowerCase();
          const nameTr = item.name && item.name.tr ? item.name.tr.toLowerCase() : "";
          const nameEn = item.name && item.name.en ? item.name.en.toLowerCase() : "";

          if (!nameTr.includes(search) && !nameEn.includes(search)) match = false;
        }
        return match;
      });
    },
    getDisplayItems(control) {
      const items = this.getFilteredItems(control);
      return items.slice(0, 3);
    },
    getHiddenCount(control) {
      if (!control.complianceItems) return 0;
      const displayedCount = this.getDisplayItems(control).length;
      return Math.max(0, control.complianceItems.length - displayedCount);
    },
    getHiddenItemsGrouped(control) {
      if (!control.complianceItems) return {};

      const displayedIds = this.getDisplayItems(control).map((i) => i._id);
      const hiddenItems = control.complianceItems.filter((i) => !displayedIds.includes(i._id));

      // Group by compliance
      const grouped = {};
      hiddenItems.forEach((item) => {
        const standard = item.compliance || "Other";
        if (!grouped[standard]) grouped[standard] = [];
        grouped[standard].push(item);
      });

      return grouped;
    },
    getCriticalityVariant(criticality) {
      switch (criticality) {
        case "LOW":
          return "success";
        case "MEDIUM":
          return "warning";
        case "HIGH":
          return "danger";
        case "CRITICAL":
          return "dark";
        default:
          return "primary";
      }
    },
    openRelations(control) {
      this.relationsModal.currentControl = control;
      this.relationsModal.complianceItems = control.complianceItems || [];
      this.relationsModal.viewState = "COMPLIANCES";
      this.viewMode = "DRILLDOWN";
    },
    goToCreate() {
      this.viewMode = "CREATE";
      this.$store.commit("organizationControl/editedEntity", null);
      this.$store.commit("organizationControl/selectedEntity", null);
    },
    onControlSaved(newControl) {
      this.viewMode = "LIST";
      this.fetchControls();
      if (newControl) {
        if (!this.selectedIds.includes(newControl._id)) {
          this.selectedIds.push(newControl._id);
          this.$set(this.selectedObjectsMap, newControl._id, newControl);
        }
        this.$toast.success(this.$t("common.save-success"));
      }
    },
    goBack() {
      if (this.relationsModal.viewState === "CONTROLS") {
        this.relationsModal.viewState = "COMPLIANCES";
      } else {
        this.viewMode = "LIST";
      }
    },
    resetToLevel() {
      this.relationsModal.viewState = "COMPLIANCES";
      this.relationsModal.currentComplianceItem = null;
    },
    async drillDownToControls(item) {
      this.relationsModal.currentComplianceItem = item;
      this.relationsModal.viewState = "CONTROLS";
      this.relationsModal.loading = true;
      this.relationsModal.relatedControls = [];

      try {
        const payload = {
          options: { page: 1, limit: 100 },
          query: {
            class: { value: "AUDIT_ACTIVITY", isObject: true },
            // status: { value: "ACTIVE", isObject: true },
            complianceItemNo: { value: item.no, isObject: true },
          },
        };

        const response = await this.$store.dispatch("organizationControl/fetchFilteredControls", payload);

        this.relationsModal.relatedControls = response.data.docs.filter((c) => c._id !== this.relationsModal.currentControl._id);
      } catch (e) {
        console.error(e);
      } finally {
        this.relationsModal.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Custom scrollbar for sidebar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}
.cursor-pointer {
  cursor: pointer;
}
</style>

<style>
/* Global style to target the modal that is appended to body */
.audit-control-modal-fullscreen .modal-dialog {
  max-width: 95vw !important;
}
</style>
